import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';

const navigation = [
  { name: 'Ana Sayfa', href: '/' },
  { name: 'Yazılar', href: '/yazilar' },
  { name: 'Hakkında', href: '/hakkinda' },
  { name: 'Bülten', href: '/bulten' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-sm shadow-editorial' 
          : 'bg-background'
      }`}
    >
      <div className="container-wide">
        <div className="flex h-16 sm:h-18 items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
          >
            {/* Typographic Logo Mark */}
            <div className="relative flex items-center justify-center w-10 h-10 bg-primary rounded-sm overflow-hidden">
              <span className="font-serif text-lg font-bold text-primary-foreground tracking-tighter">
                TAR
              </span>
              {/* Subtle pitch line accent */}
              <div className="absolute bottom-1 left-1 right-1 h-px bg-primary-foreground/30" />
            </div>
            
            {/* Logo Text */}
            <div className="hidden sm:flex flex-col">
              <span className="font-serif text-xl font-bold text-foreground tracking-tight leading-none">
                TheAnalystRoom
              </span>
              <span className="text-[10px] text-muted-foreground tracking-wider uppercase mt-0.5">
                Taktik Analiz
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-sm ${
                  isActive(item.href)
                    ? 'text-foreground bg-muted'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link to="/iletisim">
              <Button 
                variant="ghost" 
                size="sm"
                className="ml-2 text-sm font-medium text-accent hover:text-accent hover:bg-accent/10"
              >
                İletişim
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menüyü aç</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-background border-border">
              <div className="flex flex-col h-full pt-8">
                {/* Mobile Logo */}
                <div className="flex items-center gap-3 mb-8 px-2">
                  <div className="relative flex items-center justify-center w-10 h-10 bg-primary rounded-sm">
                    <span className="font-serif text-lg font-bold text-primary-foreground tracking-tighter">
                      TAR
                    </span>
                  </div>
                  <span className="font-serif text-xl font-bold text-foreground">
                    TheAnalystRoom
                  </span>
                </div>

                {/* Mobile Nav Links */}
                <nav className="flex flex-col gap-1">
                  {navigation.map((item) => (
                    <SheetClose asChild key={item.name}>
                      <Link
                        to={item.href}
                        className={`px-4 py-3 text-base font-medium transition-colors rounded-sm ${
                          isActive(item.href)
                            ? 'text-foreground bg-muted'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>

                {/* Mobile CTA */}
                <div className="mt-auto pt-8 border-t border-border">
                  <SheetClose asChild>
                    <Link to="/iletisim" className="block">
                      <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                        İletişim
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </SheetClose>
                  <p className="mt-4 text-xs text-muted-foreground text-center">
                    Futbolu positional play perspektifinden okumak
                  </p>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      {/* Bottom Border */}
      <div className="h-px bg-border" />
    </header>
  );
};

export default Header;
